'use client';

import React, { useEffect, useState } from 'react';
import BookingList from '@/components/manage-booking/bookingList';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BOOKING_TABS } from '@/lib/contants';
import { useBookingListMutation } from '@flysync/shared-redux/services/booking';
import Image from '@/lib/app-image';
import { format } from 'date-fns';
import { useRouter } from '@/lib/navigation';
import _ from 'lodash';
import { config, getTemplateAssetPath } from '@/config';

interface BookingData {
  booking_id: string;
  departure_date: string;
  booking_status: string;
  departure_time: string;
  booking_code: string;
  origin_city: string;
  destination_city: string;
  orgin_airport: string;
  orgin_terminal: string;
  duration: string;
  origin: string;
  destination: string;
  arrival_time: string;
  arrival_date: Date;
  destination_airport: string;
  destination_terminal: string;
  tripType: string;
  segments: any[];
}

function ManageBooking() {
  const router = useRouter();
  const [bookingList, { isLoading }] = useBookingListMutation();

  const [upcomingData, setUpcomingData] = useState<BookingData[]>([]);
  const [cancelledData, setCancelledData] = useState<BookingData[]>([]);
  const [completedData, setCompletedData] = useState<BookingData[]>([]);

  const [isApiCall, setIsApiCall] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [agencyId, setAgencyId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const storedToken = sessionStorage.getItem('token');
    const storedUser = sessionStorage.getItem('user');
    sessionStorage.removeItem('mailToCustomer');
    setToken(storedToken);
    if (storedUser) {
      setAgencyId(JSON.parse(storedUser)?.user_id);
    }
  }, []);

  const loader = getTemplateAssetPath('loader');

  // const bgImage = `/${config.companyName}/bg.png`;
  useEffect(() => {
    const fetchBookingList = async () => {
      try {
        const res: any = await bookingList({
          auth: token,
          data: { agencyId },
        }).unwrap();

        const uniqueData = _.uniqBy(res?.data, 'booking_id');
        const sortedData = _.orderBy(uniqueData, ['booking_id'], ['desc']);

        const result = sortedData.reduce<{
          upcomingData: BookingData[];
          cancelledData: BookingData[];
          completedData: BookingData[];
        }>(
          (acc, item) => {
            const departureDate = new Date(item.departure_date);
            departureDate.setHours(0, 0, 0, 0);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (item.booking_status === 'CNL') {
              acc.cancelledData.push(item);
            } else if (departureDate >= today) {
              acc.upcomingData.push(item);
            } else {
              acc.completedData.push(item);
            }

            return acc;
          },
          { upcomingData: [], cancelledData: [], completedData: [] },
        );

        setUpcomingData(result.upcomingData);
        setCancelledData(result.cancelledData);
        setCompletedData(result.completedData);
      } catch (error) {
        console.error('Error fetching booking list:', error);
        // Add user feedback for error
        setIsApiCall(false); // Reset API call state on error
      }
    };

    if (token && !isApiCall) {
      setIsApiCall(true);
      fetchBookingList();
    }
  }, [token, isApiCall, router, bookingList, agencyId]);

  return (
    <div className="relative bg-tertiary">
      <div
        className="h-14 w-full bg-primary bg-cover bg-center bg-no-repeat sm:h-20 lg:h-56"
        // style={{ backgroundImage: `url(${bgImage})` }}
      />
      <div className="absolute inset-x-0 top-3 mx-auto px-1 pb-4 sm:top-6 sm:w-[1200px] sm:pb-8 lg:top-12 lg:pb-20">
        <div className="mb-3 flex items-center gap-2 sm:mb-5 lg:mb-7">
          <h2 className="pl-2 text-base font-semibold text-white sm:pl-4 sm:text-lg lg:pl-8 lg:text-xl">
            My Booking
          </h2>
        </div>
        <div className="rounded-xl bg-white p-2 shadow-lg sm:p-4 lg:p-8">
          <div className="">
            {isLoading ? (
              <div className="flex h-[calc(100vh-200px)] items-center justify-center">
                <Image
                  src={loader}
                  alt="loader"
                  width={100}
                  height={100}
                  className="h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16"
                />
              </div>
            ) : (
              <Tabs defaultValue="Upcoming" className="w-full">
                <TabsList className="border-light mx-2 flex justify-between border-b-0 pb-1 sm:mx-4 sm:border-b-2 sm:pb-2 lg:mx-6">
                  {BOOKING_TABS?.map((item) => (
                    <TabsTrigger
                      key={item?.id}
                      className="items-center whitespace-nowrap border-b border-transparent px-3 pb-1 text-sm font-medium text-zinc-800 transition-colors data-[state=active]:border-primary data-[state=active]:text-primary sm:border-b-2 sm:px-4 sm:pb-2 sm:text-lg lg:border-b-4 lg:px-6 lg:text-2xl"
                      value={item?.value}
                    >
                      {item?.value}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <TabsContent
                  value="Upcoming"
                  className="px-2 pb-3 sm:px-3 sm:pb-4 lg:px-4 lg:pb-5"
                >
                  {upcomingData.length > 0 ? (
                    upcomingData?.map((item, index) => (
                      <BookingList
                        key={`${item?.booking_id}-upcoming-${index}`}
                        data={item}
                        status=""
                        className="mb-2 sm:mb-3 lg:mb-4"
                      />
                    ))
                  ) : (
                    <BookingList
                      data={{} as any}
                      status="Upcoming"
                      className="mt-2 sm:mt-3 lg:mt-4"
                    />
                  )}
                </TabsContent>
                <TabsContent
                  value="Cancelled"
                  className="px-2 pb-3 sm:px-3 sm:pb-4 lg:px-4 lg:pb-5"
                >
                  {cancelledData.length > 0 ? (
                    cancelledData?.map((item, index) => (
                      <BookingList
                        key={`${item?.booking_id}-cancelled-${index}`}
                        data={item}
                        status="Cancelled"
                        className="mb-2 sm:mb-3 lg:mb-4"
                      />
                    ))
                  ) : (
                    <BookingList
                      data={{} as any}
                      status="Cancelled"
                      className="mt-2 sm:mt-3 lg:mt-4"
                    />
                  )}
                </TabsContent>
                <TabsContent
                  value="Completed"
                  className="px-2 pb-3 sm:px-3 sm:pb-4 lg:px-4 lg:pb-5"
                >
                  {completedData.length > 0 ? (
                    completedData?.map((item, index) => (
                      <BookingList
                        key={`${item?.booking_id}-completed-${index}`}
                        data={item}
                        status="Completed"
                        className="mb-2 sm:mb-3 lg:mb-4"
                      />
                    ))
                  ) : (
                    <BookingList
                      data={{} as any}
                      status="Completed"
                      className="mt-2 sm:mt-3 lg:mt-4"
                    />
                  )}
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManageBooking;
